import React from 'react';
import { ButtonSize, ModalButtons } from '@cmhc-schl/hearth-ui';
import RichTextBody from '../../../../../../Shared_Portfolio/components/RichTextBody/RichTextBody';

export type ModalType =
  | 'unableToReRunValidationError'
  | 'cannotProceedToCreditBureauError'
  | 'unableToProceedToCreditBureauLoansCheckError'
  | 'unableToDeletePortfolioError'
  | 'cancelPortfolioError'
  | 'portfolioRequestCanRunValidation'
  | 'startRerunValidation'
  | 'portfolioRequestValidationsNotCompleted'
  | 'cannotProceedToCreditBureau'
  | 'portfolioRequestConfirmationToSendLoansToCreditBureau'
  | 'portfolioRequestConfirmationToDeletePortfolio'
  | 'deletePortfolio'
  | 'cancelationDeletePortfolioError'
  | 'canCancelPortfolio'
  | 'cancelPortfolioErrorReview'
  | 'unableToDeletePortfolioErrorForCancellation';

interface ModalData {
  id?: string | undefined;
  title?: string;
  bodyContent: React.ReactNode;
  modalButtons?: ModalButtons;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
}

const getModalContent = (
  modalType: ModalType,
  t: (key: string) => string,
  onConfirm?: () => void | Promise<void>,
  onCancel?: () => void | Promise<void>
): ModalData | null => {
  const renderRichTextBody = (translationKey: string) => (
    <RichTextBody translationKey={t(translationKey)} />
  );

  const modalContentMap: Record<ModalType, ModalData> = {
    unableToReRunValidationError: {
      id: 'portfolio-request-unable-to-rerun-validation-error',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Re-Run-Validation'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Portfolio-Id-Is-Needed'
      )
    },
    unableToProceedToCreditBureauLoansCheckError: {
      id: 'portfolio-request-loans-are-needed',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Credit-Bureau'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Re-Run-Validation-Body'
      )
    },
    portfolioRequestConfirmationToSendLoansToCreditBureau: {
      id: 'portfolio-request-confirmation-to-send-loans-to-credit-bureau',
      title: t('PI-Portfolio-Management-Request-Overview-Proceed-To-Credit-Bureau-Confirmation'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Proceed-To-Credit-Bureau-Loans'
      ),
      modalButtons: {
        primaryButton: {
          name: 'primary',
          text: { value: t('PI-Portfolio-Management-Request-Overview-Proceed-to-Credit-Bureau') },
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Proceed-to-Credit-Bureau')
          },
          onClick: onConfirm
        },
        secondaryButton: {
          size: ButtonSize.SMALL,
          name: 'deleteValidationResultsBtn',
          text: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-To-Validation-Results')
          },
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-To-Validation-Results')
          },
          onClick: onCancel
        }
      }
    },
    cancelPortfolioError: {
      id: 'portfolio-cancel-modal',
      title: t('PI-Portfolio-Management-Request-Overview-Cancel-Portfolio-Header'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Cancel-Portfolio-Error'
      )
    },
    cancelPortfolioErrorReview: {
      id: 'portfolio-cancel-modal',
      title: t('PI-Portfolio-Management-Request-Overview-Cancel-Portfolio-Header'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Cancel-Portfolio-Error-Loans-Review'
      )
    },
    deletePortfolio: {
      id: 'portfolio-the-proceed-to-credit-bureau-delete-portfolio',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Credit-Bureau'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Cannot-Delete-Portfolio'
      )
    },
    cannotProceedToCreditBureau: {
      id: 'portfolio-the-proceed-to-credit-bureau-cannot-proceed-to-credit-bureau',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Credit-Bureau'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-The-Proceed-To-Credit-Bureau'
      )
    },
    portfolioRequestConfirmationToDeletePortfolio: {
      id: 'portfolio-request-confirmation-to-delete-portfolio',
      title: t('PI-Portfolio-Management-Request-Overview-Delete-Request'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Do-You-Want-To-Delete-All-Portfolios'
      ),
      modalButtons: {
        primaryButton: {
          name: 'primary',
          text: {
            value: t('PI-Portfolio-Management-Request-Overview-Delete-Portfolio-Card')
          },
          size: ButtonSize.SMALL,
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Delete-Portfolio-Card')
          },
          onClick: onCancel
        },
        secondaryButton: {
          name: 'secondary',
          text: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-Validation-Results')
          },
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-Validation-Results')
          },
          onClick: onConfirm
        }
      }
    },
    unableToDeletePortfolioError: {
      id: 'portfolio-request-unable-to-delete-portfolio',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Delete-Portfolio-Request'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Validation-Completed-Or-Received'
      )
    },
    unableToDeletePortfolioErrorForCancellation: {
      id: 'portfolio-request-unable-to-delete-portfolio',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Delete-Portfolio-Request'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Validation-Completed'
      )
    },
    cannotProceedToCreditBureauError: {
      id: 'portfolio-request-cannot-proceed-to-credit-bureau',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Credit-Bureau'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Cannot-Proceed-To-Credit-Bureau'
      )
    },
    startRerunValidation: {
      id: 'portfolio-request-re-run-validation',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Re-Run-Validation'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Portfolio-Id-Is-Needed'
      )
    },
    portfolioRequestValidationsNotCompleted: {
      id: 'portfolio-request-validation-is-not-completed',
      title: t('PI-Portfolio-Management-Request-Overview-Unable-To-Re-Run-Validation'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-User-Has-Read-Access-Only'
      )
    },
    portfolioRequestCanRunValidation: {
      id: 'portfolioRequestCanRunValidation',
      title: t('PI-Portfolio-Management-Request-Overview-Re-Run-Validation-Confirmation'),
      bodyContent: renderRichTextBody('PI-Portfolio-Management-Request-Overview-Re-Run-Validation'),
      modalButtons: {
        primaryButton: {
          name: 'reRunValidationBtn',
          text: { value: t('PI-Portfolio-Management-Request-Overview-Re-Run-Validation-Button') },
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Re-Run-Validation-Button')
          },
          onClick: onConfirm
        },
        secondaryButton: {
          size: ButtonSize.SMALL,
          name: 'deleteValidationResultsBtn',
          text: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-To-Validation-Results')
          },
          ariaText: {
            value: t('PI-Portfolio-Management-Request-Overview-Return-To-Validation-Results')
          },
          onClick: onCancel
        }
      }
    },
    cancelationDeletePortfolioError: {
      id: 'cancelationDeletePortfolioError',
      title: t('PI-Portfolio-Management-Request-Overview-Cancelation-Delete-Header'),
      bodyContent: renderRichTextBody(
        'PI-Portfolio-Management-Request-Overview-Cancelation-Delete-Body'
      )
    },
    canCancelPortfolio: {
      id: 'canCancelPortfolio',
      title: t('Can-Cancel-Porfolio-Header'),
      bodyContent: renderRichTextBody('Can-Cancel-Porfolio-SubHeader'),
      modalButtons: {
        primaryButton: {
          name: 'canCancelBtn',
          text: { value: t('Can-Cancel-Portfolio-Btn') },
          ariaText: {
            value: t('Can-Cancel-Portfolio-Btn')
          },
          onClick: onConfirm
        },
        secondaryButton: {
          size: ButtonSize.SMALL,
          name: 'canCancelBtn',
          text: {
            value: t('Cancel')
          },
          ariaText: {
            value: t('Cancel')
          },
          onClick: onCancel
        }
      }
    }
  };
  return modalContentMap[modalType] || null;
};

export default getModalContent;
